import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import {
  Coins,
  TrendingUp,
  TrendingDown,
  Clock,
  Gift,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const TYPE_CONFIG = {
  earn:          { icon: TrendingUp,   color: 'text-[#059669]',  label: 'Earned',       sign: '+' },
  spend:         { icon: TrendingDown, color: 'text-[#DC2626]',  label: 'Spent',        sign: '-' },
  expire:        { icon: Clock,        color: 'text-[#9CA3AF]',  label: 'Expired',      sign: '-' },
  signup_bonus:  { icon: Gift,         color: 'text-[#C9956C]',  label: 'Sign-up Bonus',sign: '+' },
};

const SOURCE_LABELS = {
  signup_bonus: 'Sign-up bonus',
  purchase:     'Purchase',
  sale:         'Sale',
  boost:        'Item boost',
  expiry:       'Points expired',
};

const PAGE_SIZE = 20;

const FILTER_OPTIONS = [
  { value: 'all',    label: 'All' },
  { value: 'earn',   label: 'Earned' },
  { value: 'spend',  label: 'Spent' },
  { value: 'expire', label: 'Expired' },
];

export default function PointsPage() {
  const { api } = useAuth();
  const [balance, setBalance] = useState(null);
  const [entries, setEntries] = useState([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/points/balance').then(r => setBalance(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setEntries([]);
    setSkip(0);
    setHasMore(true);
    fetchPage(0, true);
  }, [filter]);

  async function fetchPage(skipVal, reset = false) {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const r = await api.get('/points/history', {
        params: { limit: PAGE_SIZE, skip: skipVal },
      });
      const data = r.data.entries || [];
      const filtered = filter === 'all' ? data : data.filter(e => e.type === filter);
      setEntries(prev => reset ? filtered : [...prev, ...filtered]);
      if (data.length < PAGE_SIZE) setHasMore(false);
      setSkip(skipVal + PAGE_SIZE);
    } catch {
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  const typeConfig = (entry) =>
    entry.type === 'earn' && entry.source === 'signup_bonus'
      ? TYPE_CONFIG.signup_bonus
      : TYPE_CONFIG[entry.type] || TYPE_CONFIG.earn;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--theme-text-primary)] font-['Manrope']">
            Points
          </h1>
          <p className="text-sm text-[var(--theme-text-muted)] mt-0.5">
            Earn points on every purchase and sale.
          </p>
        </div>

        {/* Balance card */}
        <div className="p-5 rounded-2xl border border-[var(--theme-border)] bg-[var(--theme-card)] mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
              <Coins className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-[var(--theme-text-muted)] font-['Manrope']">Current Balance</p>
              <p className="text-2xl font-bold text-[var(--theme-text-primary)] font-['Manrope']">
                {balance?.points_balance?.toLocaleString() ?? '—'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-xl bg-[var(--theme-hover)]">
              <p className="text-[10px] text-[var(--theme-text-muted)] uppercase tracking-wide">Lifetime Earned</p>
              <p className="text-lg font-bold text-[var(--theme-text-primary)] font-['Manrope'] mt-0.5">
                {balance?.points_lifetime_earned?.toLocaleString() ?? '—'}
              </p>
            </div>
            <div className="p-3 rounded-xl bg-[var(--theme-hover)]">
              <p className="text-[10px] text-[var(--theme-text-muted)] uppercase tracking-wide">Redeem Rate</p>
              <p className="text-lg font-bold text-[var(--theme-text-primary)] font-['Manrope'] mt-0.5">
                100 pts = $1
              </p>
            </div>
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
          {FILTER_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilter(opt.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors
                          ${filter === opt.value
                            ? 'bg-[#1E3A5F] text-white'
                            : 'bg-[var(--theme-hover)] text-[var(--theme-text-secondary)] hover:bg-[var(--theme-border)]'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Transaction list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-[var(--theme-text-muted)]" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Coins className="w-10 h-10 mx-auto text-[var(--theme-text-muted)] mb-3" />
            <p className="text-[var(--theme-text-secondary)] text-sm">No transactions yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map(entry => {
              const cfg = typeConfig(entry);
              const Icon = cfg.icon;
              const absAmount = Math.abs(entry.amount);
              return (
                <div
                  key={entry.id}
                  className="flex items-center gap-3 p-3 rounded-xl bg-[var(--theme-card)]
                             border border-[var(--theme-border)] hover:bg-[var(--theme-hover)]
                             transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-[var(--theme-hover)] flex items-center justify-center flex-shrink-0">
                    <Icon className={`w-4 h-4 ${cfg.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[var(--theme-text-primary)] font-['Manrope']">
                      {SOURCE_LABELS[entry.source] || entry.source}
                    </p>
                    <p className="text-xs text-[var(--theme-text-muted)]">
                      {new Date(entry.created_at).toLocaleDateString('en-AU', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                      {entry.expires_at && entry.type === 'earn' && (
                        <span className="ml-2 opacity-60">
                          Expires {new Date(entry.expires_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-sm font-bold font-['Manrope'] ${cfg.color}`}>
                      {cfg.sign}{absAmount.toLocaleString()} pts
                    </p>
                    <p className="text-[10px] text-[var(--theme-text-muted)]">
                      Balance: {entry.balance_after?.toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load more */}
        {hasMore && !loading && (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              onClick={() => fetchPage(skip)}
              disabled={loadingMore}
              className="rounded-full border-[var(--theme-border)]"
            >
              {loadingMore ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ChevronDown className="w-4 h-4 mr-2" />
              )}
              Load more
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
